// COMO um compromisso da agenda se pinta na grade do dia — a regra, num lugar so.
//
// Cores escolhidas por ele em 19/08/2026, ao pedir a grade do dia (07h–19h):
//   *"Fundo Transparente, fonte preta: Antes da data e hora / Fundo Amarelo, fonte
//    Preta: Uma hora ou menos antes do vencimento / Fundo Vermelho fonte Branca:
//    Vencido / Fundo Azul fonte Preta: Concluído / Fundo Laranja fonte Preta:
//    Cancelado"*
//
// ⚠️ **Duas naturezas diferentes moram no mesmo enum, e e' de proposito.**
// `concluido` e `cancelado` sao DECISOES de uma pessoa e vivem no banco;
// `vencido`, `perto` e `futuro` sao funcao do RELOGIO e nao existem em coluna
// nenhuma (ver o cabecalho da migration 0240). A tela nao quer saber dessa
// diferenca — ela quer uma cor —, e por isso a juncao acontece aqui, uma vez, em vez
// de virar um `if` espalhado por cada lugar que desenha um compromisso.
//
// ⚠️ **A ORDEM de decisao e' o que faz sentido, nao a lista.** Decisao humana vence
// relogio: um compromisso cancelado das 10:00 continua CANCELADO as 11:00, nunca
// "vencido". Cancelado que vira vermelho sozinho ao meio-dia acusaria ele de ter
// perdido um compromisso que ele mesmo desmarcou.
//
// ⛔ Sem `Date.now()` aqui dentro: o `agora` entra por parametro, como no
// `agenda-aviso.ts`. E' o que deixa o teste escrever qualquer instante sem congelar
// relogio.

/**
 * O que uma pessoa decidiu sobre o compromisso. E' o que o banco guarda.
 *
 * ⚠️ **'vencido' entrou em 19/08/2026 (migration 0242) e NAO revoga a nota acima.**
 * Sao dois "vencido" diferentes: o do RELOGIO (`marcado` + hora passada, calculado,
 * sem coluna) quer dizer *"passou e voce ainda nao disse o que foi"*; o daqui e' um
 * DESFECHO que ele escolheu — *"passou e nao aconteceu"* —, irmao de 'concluido' e
 * 'cancelado'. Ninguem escreve 'vencido' sem um dedo tocando o chip, que e'
 * exatamente o que a [DA-581] recusou automatizar.
 */
export type StatusCompromisso = 'marcado' | 'concluido' | 'cancelado' | 'vencido';

/**
 * A REGUA DE ANDAMENTO da folha, na ordem em que ela se le.
 *
 * Escolha dele, 19/08/2026: *"Os 4 tocaveis. So Antes do Vencimento que nao
 * aparece."* ⚠️ **`perto` (o amarelo) fica de fora de proposito** — ele nao e'
 * desfecho nenhum, e' o relogio dizendo que falta menos de uma hora. Um chip pra ele
 * ofereceria um toque que nao decide nada, e a fila e' de decisoes.
 *
 * ⚠️ **A ordem e' cronologica, nao alfabetica**: agendado → vencido → concluido →
 * cancelado percorre o que acontece com um compromisso, e e' o que faz a fila
 * horizontal poder ser lida de relance sem procurar.
 *
 * Mora aqui, e nao na tela, porque o `zod` da rota valida contra esta MESMA lista —
 * duas copias divergem, e a que some e' a do servidor (mesmo motivo do
 * `agenda-aviso.ts`).
 */
export const ANDAMENTOS: ReadonlyArray<{ status: StatusCompromisso; rotulo: string }> = [
  { status: 'marcado', rotulo: 'Agendado' },
  { status: 'vencido', rotulo: 'Vencido' },
  { status: 'concluido', rotulo: 'Concluído' },
  { status: 'cancelado', rotulo: 'Cancelado' },
];

/**
 * Como o compromisso aparece na grade.
 *
 * ⚠️ Isto NAO e' o `status`: e' o status **somado ao relogio**. Os tres ultimos so
 * existem para `status = 'marcado'`.
 */
export type EstadoCompromisso =
  /** Ele marcou como feito. */
  | 'concluido'
  /** Ele desmarcou. */
  | 'cancelado'
  /** A hora passou e ninguem disse o que aconteceu. */
  | 'vencido'
  /** Falta uma hora ou menos. */
  | 'perto'
  /** Ainda vai acontecer, com folga. */
  | 'futuro';

/** A janela do "esta chegando", em minutos. Escolha dele: uma hora. */
export const MINUTOS_PERTO = 60;

/**
 * As cores da grade — as dele, sem tradução.
 *
 * ⚠️ `fundo: null` e' o "transparente" que ele pediu, nao um branco parecido: o
 * cartao tem que deixar ver a linha da hora atras dele, senao a faixa vazia e a
 * faixa ocupada ficam com o mesmo peso e a grade para de se ler como grade.
 */
export interface PinturaCompromisso {
  fundo: string | null;
  texto: string;
  /** A borda so existe no transparente — sem ela o cartao nao tem contorno nenhum. */
  borda: string | null;
  /**
   * A MESMA cor, na versao que se le sobre BRANCO — borda e letra de um chip apagado.
   *
   * ⚠️ **Nao e' o `fundo` reaproveitado, e o cancelado explica por que.** O laranja
   * `#fdba74` foi escolhido pra receber letra preta EM CIMA dele; usado como letra
   * sobre branco da' ~1.9:1, que ninguem le. Cada estado precisa das duas versoes da
   * sua cor, e elas moram juntas pra nao existir um literal escuro escrito na tela.
   *
   * ⚠️ No `futuro` isto e' um cinza de proposito: a ausencia de cor E' o estado dele
   * na grade (escolha do Ruan), e inventar uma cor aqui faria a regua ANDAMENTO
   * prometer um estado colorido que o cartao nao tem.
   */
  contorno: string;
  /**
   * O selo curto que acompanha a cor.
   *
   * ⚠️ **Nao e' enfeite nem redundancia preguicosa: e' o que torna a grade legivel
   * pra ele.** Ele nao distingue verde de vermelho ([[user_daltonismo]]), e a
   * paleta que ele escolheu tem vermelho, laranja e amarelo em sequencia — os tres
   * mais proximos entre si justamente pra quem enxerga assim. A cor fica como ele
   * pediu; o selo entra POR CIMA dela, nunca no lugar dela
   * ([[feedback_acrescentar_redundancia_nao_trocar]]).
   */
  selo: string | null;
}

/**
 * O estado de um compromisso agora.
 *
 * @param quando  o instante marcado
 * @param status  o que uma pessoa decidiu (o que esta no banco)
 * @param agora   o relogio
 */
export function estadoDoCompromisso(
  quando: Date,
  status: StatusCompromisso,
  agora: Date,
): EstadoCompromisso {
  // Decisao humana primeiro — ver o cabecalho.
  if (status === 'concluido') return 'concluido';
  if (status === 'cancelado') return 'cancelado';
  // ⚠️ O vencido ESCOLHIDO tambem vence o relogio, e isso importa no sentido que
  // ninguem espera: um compromisso marcado como vencido e ADIADO pra semana que vem
  // continuaria vermelho. E' o certo — quem adiou sem voltar pra "agendado" nao
  // desfez o desfecho, e a tela nao pode dizer por ele que agora vai acontecer.
  if (status === 'vencido') return 'vencido';

  const faltamMs = quando.getTime() - agora.getTime();
  if (faltamMs < 0) return 'vencido';
  if (faltamMs <= MINUTOS_PERTO * 60_000) return 'perto';
  return 'futuro';
}

/** A cor de cada estado. */
export function pinturaDoEstado(estado: EstadoCompromisso): PinturaCompromisso {
  switch (estado) {
    case 'concluido':
      // ⚠️ **O azul escureceu em 19/08/2026, e quem mandou foi a FONTE.** Ele
      // reespecificou a paleta como *"Concluido: Fundo Azul fonte Branca"* — e branco
      // sobre o `#bfdbfe` de antes (que nasceu com fonte preta) tem contraste de
      // ~1.4:1, ilegivel ao sol do balcao. Trocar so a fonte teria cumprido a letra
      // do pedido e entregado um cartao que nao se le; o azul foi junto.
      return { fundo: '#1d4ed8', texto: '#ffffff', borda: null, contorno: '#1d4ed8', selo: 'feito' };
    case 'cancelado':
      // O contorno e' MAIS ESCURO que o fundo: o `#fdba74` como letra sobre branco
      // nao se le. Ver o campo `contorno`.
      return { fundo: '#fdba74', texto: '#111111', borda: null, contorno: '#c2410c', selo: 'cancelado' };
    case 'vencido':
      return { fundo: '#dc2626', texto: '#ffffff', borda: null, contorno: '#dc2626', selo: 'vencido' };
    case 'perto':
      return { fundo: '#fde68a', texto: '#111111', borda: null, contorno: '#a16207', selo: null };
    case 'futuro':
    default:
      return { fundo: null, texto: '#111111', borda: '#e0ddd9', contorno: '#78716c', selo: null };
  }
}

/**
 * O selo do cartao, ja escrito.
 *
 * ⚠️ No 'perto' o selo e' a CONTA, nao a palavra: *"40 min"* diz uma coisa que a cor
 * amarela nao diz — quanto tempo sobrou. Um rotulo fixo ("em breve") gastaria o
 * mesmo espaco pra repetir o que a cor ja tinha dito.
 */
export function seloDoCompromisso(
  quando: Date,
  status: StatusCompromisso,
  agora: Date,
): string | null {
  const estado = estadoDoCompromisso(quando, status, agora);
  if (estado !== 'perto') return pinturaDoEstado(estado).selo;

  const minutos = Math.max(0, Math.round((quando.getTime() - agora.getTime()) / 60_000));
  // 60 minutos exatos ainda e' "1 h" — arredondar pra "60 min" ficaria mais comprido
  // e diria a mesma coisa.
  return minutos >= 60 ? '1 h' : `${minutos} min`;
}

/**
 * A FAIXA DE HORAS que a grade do dia desenha.
 *
 * Base 07–19, escolha dele. ⚠️ **Mas a faixa ESTICA pra caber o que existe**: um
 * compromisso as 20:30 nao pode sumir da tela porque cai fora do horario comercial —
 * agenda que esconde compromisso e' pior que agenda nenhuma, porque ele confia nela.
 * Esticar so pra baixo/pra cima do necessario mantem o dia normal com as 13 faixas
 * de sempre.
 *
 * @returns `[primeiraHora, ultimaHora]`, ambas inclusivas
 */
export const HORA_GRADE_INICIO = 7;
export const HORA_GRADE_FIM = 19;

export function faixaDaGrade(horasDosCompromissos: number[]): [number, number] {
  let inicio = HORA_GRADE_INICIO;
  let fim = HORA_GRADE_FIM;
  for (const h of horasDosCompromissos) {
    if (!Number.isFinite(h)) continue;
    if (h < inicio) inicio = Math.max(0, Math.floor(h));
    if (h > fim) fim = Math.min(23, Math.floor(h));
  }
  return [inicio, fim];
}
